import React, { useEffect, useRef } from "react";

/**
 * Iter75.4 — hCaptcha widget wrapper.
 *
 * The widget script (`https://js.hcaptcha.com/1/api.js`) is loaded on
 * demand — the login page only mounts this component once the user has
 * 3+ failed attempts on file. The `onVerify` prop is called with the
 * one-time token the backend will hand off to hCaptcha's `/siteverify`.
 *
 * In dev, `siteKey` is empty and the WARN log on the backend is loud.
 * This component renders a small disclosure notice instead of the
 * widget so the login form doesn't hang waiting for a widget that
 * will never load.
 */
export default function HCaptchaWidget({ siteKey, onVerify }) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);

    useEffect(() => {
        if (!siteKey) return undefined;
        let cancelled = false;

        const mount = () => {
            if (cancelled) return;
            if (!window.hcaptcha || !containerRef.current) return;
            try {
                widgetIdRef.current = window.hcaptcha.render(containerRef.current, {
                    sitekey: siteKey,
                    callback: (token) => onVerify?.(token),
                    "expired-callback": () => onVerify?.(""),
                    "error-callback": () => onVerify?.(""),
                });
            } catch (_e) {
                // Widget already mounted or script race — ignore.
            }
        };

        if (window.hcaptcha) {
            mount();
        } else {
            const existing = document.querySelector("script[data-asl-hcaptcha]");
            if (existing) {
                existing.addEventListener("load", mount, { once: true });
            } else {
                const s = document.createElement("script");
                s.src = "https://js.hcaptcha.com/1/api.js?render=explicit";
                s.async = true;
                s.defer = true;
                s.setAttribute("data-asl-hcaptcha", "1");
                s.addEventListener("load", mount, { once: true });
                document.head.appendChild(s);
            }
        }

        return () => {
            cancelled = true;
            const id = widgetIdRef.current;
            if (id != null && window.hcaptcha?.reset) {
                try { window.hcaptcha.reset(id); } catch (_e) { /* ignore */ }
            }
        };
    }, [siteKey, onVerify]);

    if (!siteKey) {
        return (
            <div
                className="rounded-md border px-3 py-2 text-xs"
                style={{
                    background: "rgba(255,255,255,0.10)",
                    borderColor: "rgba(255,255,255,0.20)",
                    color: "rgba(255,255,255,0.75)",
                }}
                data-testid="hcaptcha-dev-notice"
            >
                Additional verification will be required in production.
                <br />(Dev mode: CAPTCHA bypass active.)
            </div>
        );
    }

    return (
        <div className="flex justify-center" data-testid="hcaptcha-widget">
            <div ref={containerRef} />
        </div>
    );
}
